﻿
/**
 * Bia Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useBiaStore } from '../../store/bia.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { Logger } from '@/lib/app-log.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class BiaIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getErrorMessage(error) {
        if (error?.code === 'BIA_ID_DUPLICATE' || error?.response?.data?.code === 'BIA_ID_DUPLICATE') {
            return i18next.t('bcmBia:messages.biaIdDuplicate', { defaultValue: 'This BIA ID is already in use' });
        }
        return getIntegrationErrorMessage(error);
    }

    _extractErrorMeta(error) {
        const data = error.response?.data;
        return {
            message: this._getErrorMessage(error),
            code: data?.code || null,
        };
    }

    /**
     * Fetch all items and update store
     */
    async fetchBias(params = {}) {
        const store = useBiaStore.getState();

        if (store.setLoading) store.setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getAll(params);

            // Handle various setter naming conventions
            if (store.setBias) {
                store.setBias(response.data);
            } else if (store.setItems) {
                store.setItems(response.data);
            }

            // Handle Pagination
            if (response.meta || response.pagination) {
                const paginationData = response.meta || response.pagination;
                const currentPage = parseInt(paginationData.page || paginationData.currentPage || 1);
                const itemsPerPage = parseInt(paginationData.limit || paginationData.pageSize || 10);
                const totalItems = parseInt(paginationData.total || 0);
                const totalPages = parseInt(paginationData.pages || paginationData.totalPages || Math.ceil(totalItems / itemsPerPage) || 0);

                const mappedPagination = {
                    currentPage,
                    itemsPerPage,
                    totalItems,
                    totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1,
                    sortBy: params.sortBy || paginationData.sortBy || null,
                    sortOrder: params.sortOrder || paginationData.sortOrder || 'desc'
                };

                if (store.setPagination) store.setPagination(mappedPagination);
                else if (store.setPaginationData) store.setPaginationData(mappedPagination);
            }

            // Handle Unique Values (if applicable)
            if (response.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchBiaById(id) {
        const store = useBiaStore.getState();
        if (store.setLoading) store.setLoading(true);
        if (store.setSelectedItem) store.setSelectedItem(null);

        try {
            const response = await this.service.getById(id);

            // Try common naming for selecting single item
            if (store.setSelectedBia) {
                store.setSelectedBia(response.data);
            } else if (store.setCurrentBia) {
                store.setCurrentBia(response.data);
            } else if (store.setSelectedItem) {
                store.setSelectedItem(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createBia(data) {
        const store = useBiaStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.create(data);

            toast.success(
                i18next.t('bcmBia:messages.createSuccess', { defaultValue: 'BIA created' }),
            );

            // Optional: Close form if store has that action
            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const { message, code } = this._extractErrorMeta(error);
            toast.error(message);
            return { success: false, error: message, code };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Update item
     * @param {object} [options] - silent: no success toast; noLoading: no full-page loading
     */
    async updateBia(id, data, options = {}) {
        const { silent = false, noLoading = false } = options;
        const store = useBiaStore.getState();
        if (!noLoading && store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            if (!silent) {
                toast.success(
                    i18next.t('bcmBia:messages.updateSuccess', { defaultValue: 'BIA updated' }),
                );
            }

            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const { message, code } = this._extractErrorMeta(error);
            toast.error(message);
            return { success: false, error: message, code };
        } finally {
            if (!noLoading && store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteBia(id) {
        const store = useBiaStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:biaDeleted', { defaultValue: 'Bia deleted' }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Batch delete items
     */
    async batchDeleteBias(ids) {
        const store = useBiaStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            await this.service.batchDelete(ids);

            toast.success(
                i18next.t('bcmBia:messages.bulkDeleteSuccess', {
                        defaultValue: '{{count}} records deleted',
                        count: ids.length,
                    }),
            );

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Fetch BIAs for Overall Impacts table (separate pagination)
     */
    async fetchOverallImpacts(params = {}) {
        const store = useBiaStore.getState();

        try {
            const response = await this.service.getAll(params);

            if (store.setOverallData) {
                store.setOverallData(response.data);
            }

            // Handle Pagination for overall table
            if (response.meta || response.pagination) {
                const paginationData = response.meta || response.pagination;
                const currentPage = parseInt(paginationData.page || paginationData.currentPage || 1);
                const itemsPerPage = parseInt(paginationData.limit || paginationData.pageSize || 10);
                const totalItems = parseInt(paginationData.total || 0);
                const totalPages = parseInt(paginationData.pages || paginationData.totalPages || Math.ceil(totalItems / itemsPerPage) || 0);

                const mappedPagination = {
                    currentPage,
                    itemsPerPage,
                    totalItems,
                    totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1,
                    sortBy: params.sortBy || paginationData.sortBy || null,
                    sortOrder: params.sortOrder || paginationData.sortOrder || 'desc'
                };

                if (store.setOverallPagination) store.setOverallPagination(mappedPagination);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] fetchOverallImpacts error:', { error: message });
            return { success: false, error: message };
        }
    }

    async fetchStats() {
        const store = useBiaStore.getState();
        try {
            if (!this.service.getStats) {
                return { success: false, error: 'getStats not available' };
            }
            const body = await this.service.getStats();
            let payload = body;
            if (
                body &&
                typeof body === 'object' &&
                'data' in body &&
                body.data != null &&
                typeof body.data === 'object' &&
                !Array.isArray(body.data)
            ) {
                payload = body.data;
            }
            if (store.setStats) store.setStats(payload ?? null);
            return { success: true, data: payload };
        } catch (error) {
            Logger.error('BiaIntegration.fetchStats failed', { error: error?.message });
            return { success: false, error: error.message };
        }
    }

    /**
     * Get related processes for a BIA (paginated, searchable)
     */
    async getRelatedProcesses(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedProcesses(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] getRelatedProcesses error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    /**
     * Link processes to a BIA
     */
    async addProcesses(biaId, processIds, opts = {}) {
        try {
            const response = await this.service.addProcesses(biaId, processIds);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:processesLinkedSuccessfully', { defaultValue: 'Processes linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Unlink a process from a BIA
     */
    async removeProcess(biaId, processId) {
        try {
            const response = await this.service.removeProcess(biaId, processId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Get related assets for a BIA (paginated, searchable)
     */
    async getRelatedAssets(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedAssets(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] getRelatedAssets error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    /**
     * Link assets to a BIA
     */
    async addAssets(biaId, assetIds, opts = {}) {
        try {
            const response = await this.service.addAssets(biaId, assetIds);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:assetsLinkedSuccessfully', { defaultValue: 'Assets linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Unlink an asset from a BIA
     */
    async removeAsset(biaId, assetId) {
        try {
            const response = await this.service.removeAsset(biaId, assetId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Get related vendors for a BIA (paginated, searchable)
     */
    async getRelatedVendors(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedVendors(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] getRelatedVendors error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    /**
     * Link vendors to a BIA
     */
    async addVendors(biaId, vendorIds, opts = {}) {
        try {
            const response = await this.service.addVendors(biaId, vendorIds);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:vendorsLinkedSuccessfully', { defaultValue: 'Vendors linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Unlink a vendor from a BIA
     */
    async removeVendor(biaId, vendorId) {
        try {
            const response = await this.service.removeVendor(biaId, vendorId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Get related recovery resources for a BIA (paginated, searchable)
     */
    async getRelatedRecoveryResources(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedRecoveryResources(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] getRelatedRecoveryResources error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    /**
     * Link recovery resources to a BIA
     */
    async addRecoveryResources(biaId, recoveryResourceIds) {
        try {
            const response = await this.service.addRecoveryResources(biaId, recoveryResourceIds);
            toast.success(i18next.t('integrationToasts:recoveryresourcesLinkedSuccessfully', { defaultValue: 'RecoveryResources linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Unlink a recovery resource from a BIA
     */
    async removeRecoveryResource(biaId, resourceId) {
        try {
            const response = await this.service.removeRecoveryResource(biaId, resourceId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Get related RPO calculations for a BIA (paginated, searchable)
     */
    async getRelatedRpoCalculations(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedRpoCalculations(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] getRelatedRpoCalculations error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    /**
     * Link RPO calculations to a BIA
     */
    async addRpoCalculations(biaId, calculationIds) {
        try {
            const response = await this.service.addRpoCalculations(biaId, calculationIds);
            toast.success(i18next.t('integrationToasts:rpoCalculationsLinkedSuccessfully', { defaultValue: 'RPO calculations linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Unlink an RPO calculation from a BIA
     */
    async removeRpoCalculation(biaId, calculationId) {
        try {
            const response = await this.service.removeRpoCalculation(biaId, calculationId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Get related RPO recommendations for a BIA (paginated, searchable)
     */
    async getRelatedRpoRecommendations(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedRpoRecommendations(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] getRelatedRpoRecommendations error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    /**
     * Link RPO recommendations to a BIA
     */
    async addRpoRecommendations(biaId, recommendationIds) {
        try {
            const response = await this.service.addRpoRecommendations(biaId, recommendationIds);
            toast.success(i18next.t('integrationToasts:rpoRecommendationsLinkedSuccessfully', { defaultValue: 'RPO recommendations linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Unlink an RPO recommendation from a BIA
     */
    async removeRpoRecommendation(biaId, recommendationId) {
        try {
            const response = await this.service.removeRpoRecommendation(biaId, recommendationId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // ==========================================
    // BIA DOCUMENTS
    // ==========================================

    /**
     * Get paginated related documents for a BIA
     */
    async getRelatedDocuments(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedDocuments(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BIA Integration] getRelatedDocuments error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    /**
     * Link documents to a BIA
     */
    async addDocuments(biaId, documentIds) {
        try {
            const response = await this.service.addDocuments(biaId, documentIds);
            toast.success(i18next.t('integrationToasts:documentsLinkedSuccessfully', { defaultValue: 'Documents linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Unlink a document from a BIA
     */
    async removeDocument(biaId, documentId) {
        try {
            const response = await this.service.removeDocument(biaId, documentId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getRelatedRisks(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedRisks(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addRisks(biaId, riskIds, opts = {}) {
        try {
            const response = await this.service.addRisks(biaId, riskIds);
            if (!opts.silent) {
                toast.success(
                    i18next.t('bcmBia:messages.risksLinkedSuccess', {
                        defaultValue: 'Risks linked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeRisk(biaId, riskId, opts = {}) {
        try {
            const response = await this.service.removeRisk(biaId, riskId);
            if (!opts.silent) {
                toast.success(
                    i18next.t('bcmBia:messages.riskUnlinkedSuccess', {
                        defaultValue: 'Risk unlinked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getRelatedBcps(biaId, params = {}) {
        try {
            const response = await this.service.getRelatedBcps(biaId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addBcps(biaId, bcpIds, opts = {}) {
        try {
            const response = await this.service.addBcps(biaId, bcpIds);
            if (!opts.silent) {
                toast.success(
                    i18next.t('bcmBia:messages.bcpsLinkedSuccess', {
                        defaultValue: 'BCPs linked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeBcp(biaId, bcpId, opts = {}) {
        try {
            const response = await this.service.removeBcp(biaId, bcpId);
            if (!opts.silent) {
                toast.success(
                    i18next.t('bcmBia:messages.bcpUnlinkedSuccess', {
                        defaultValue: 'BCP unlinked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    notifyPageError(error) {
        toast.error(getIntegrationErrorMessage(error));
    }

    notifyDependencyUnlinkSuccess(nameSuffix = '') {
        const base = i18next.t('bcmCommon:messages.unlinkSuccess', {
            defaultValue: 'Disconnected',
        });
        toast.success(nameSuffix && nameSuffix !== '-' ? `${base}: ${nameSuffix}` : base);
    }

    notifyDependencyUnlinkFailed(error) {
        toast.error(getIntegrationErrorMessage(error));
    }

    notifyDependencyBulkUnlinkSuccess(count) {
        toast.success(
            i18next.t('bcmCommon:messages.bulkUnlinkSuccess', {
                count,
                defaultValue: '{{count}} links removed successfully.',
            }),
        );
    }

    notifyBulkDeleteSuccess() {
        toast.success(
            i18next.t('bcmCommon:messages.deleteSuccess', { defaultValue: 'Deleted' }),
        );
    }

    notifyDocumentsLinkSuccess(count) {
        toast.success(
            i18next.t('bcmBia:detail.dependencies.messages.linkSuccess', {
                count,
                defaultValue: '{{count}} records linked',
            }),
        );
    }

    notifyDependenciesLinked(count) {
        toast.success(i18next.t('bcmCommon:messages.risksLinked', { defaultValue: 'Linked' }), {
            description: i18next.t('bcmCommon:messages.itemsAdded', {
                count,
                defaultValue: '{{count}} new items added.',
            }),
        });
    }

    notifyReportCreated() {
        toast.success(
            i18next.t('bcmCommon:messages.createSuccess', { defaultValue: 'Created' }),
        );
    }

    notifyReportDeleted() {
        toast.success(
            i18next.t('bcmCommon:messages.deleteSuccess', { defaultValue: 'Deleted' }),
        );
    }

    notifyDocumentUnlinkSuccess() {
        toast.success(
            i18next.t('bcmBia:detail.dependencies.messages.unlinkSuccess', {
                defaultValue: 'Link removed',
            }),
        );
    }

    async checkBiaIdAvailability(biaId, excludeId = null) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, available: true };
        }
        const trimmed = String(biaId || '').trim();
        if (!trimmed) {
            return { success: true, available: true };
        }
        try {
            const response = await this.service.checkBiaIdAvailability({
                biaId: trimmed,
                ...(excludeId ? { excludeId } : {}),
            });
            return {
                success: true,
                available: response.data?.available !== false,
            };
        } catch (error) {
            return { success: false, available: true, error: this._getErrorMessage(error) };
        }
    }

    async fetchBiaPartyTemplate(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: null };
        }
        try {
            const response = await this.service.getPartyTemplate(params);
            return {
                success: response.success !== false,
                data: response.data,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: null, error: message };
        }
    }

    async fetchBiaParty(biaId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !biaId) {
            return { success: false, data: null, partyTemplate: null };
        }
        try {
            const response = await this.service.getParty(biaId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: null, partyTemplate: null, error: message };
        }
    }

    async syncBiaParty(biaId, payload, options = {}) {
        const { silentSuccess = false } = options;
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !biaId) {
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }
        try {
            const response = await this.service.syncParty(biaId, payload);
            if (!silentSuccess) {
                toast.success(
                    i18next.t('bcmBia:messages.partySaveSuccess', {
                            defaultValue: 'Ownership and roles saved',
                        }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async submitBia(biaId) {
        try {
            const response = await this.service.submit(biaId);
            toast.success(i18next.t('bcmBia:messages.submitSuccess', { defaultValue: 'Record submitted for approval' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
